import { Activity, Eye, Flame, MessageSquare, RefreshCw } from "lucide-react";

const SLIDE_TITLES_SHORT = [
  "Title", "Problem", "Stakes", "AI Question", "Guide",
  "Plan", "Closed Systems", "vs. DIY+AI", "Workflows", "Team",
  "Trusted", "Without/With", "Enterprise", "Calculator", "FAQ", "Contact",
];

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function fmtSec(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60); const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function ActivityRow({ ev }) {
  const p = ev.prospect;
  const Avatar = () => p?.logo_url ? (
    <img src={p.logo_url} alt="" className="h-9 w-9 rounded-full object-cover border border-ink-900/10 flex-shrink-0" />
  ) : (
    <div className="h-9 w-9 rounded-full grid place-items-center serif text-sm text-sun-500 bg-sun-500/12 border border-sun-500/25 flex-shrink-0">
      {p?.company_name?.[0] || "?"}
    </div>
  );
  let icon, line;
  if (ev.kind === "visit") {
    icon = <Eye className="h-3.5 w-3.5 text-sun-500" />;
    line = <span><strong className="text-ink-900">{p.company_name}</strong> opened the deck <span className="text-ink-900/55">· visit {ev.view_count}</span></span>;
  } else if (ev.kind === "slide") {
    icon = <Activity className="h-3.5 w-3.5 text-sun-500" />;
    const title = SLIDE_TITLES_SHORT[ev.slide_index] || `Slide ${ev.slide_index + 1}`;
    line = <span><strong className="text-ink-900">{p.company_name}</strong> viewed <em className="text-sun-500/90">{title}</em> for <span className="text-ink-900">{fmtSec(ev.seconds)}</span></span>;
  } else if (ev.kind === "section") {
    const hot = (ev.seconds || 0) >= 60;
    icon = hot ? <Flame className="h-3.5 w-3.5 text-red-500" /> : <Activity className="h-3.5 w-3.5 text-sun-500" />;
    line = (
      <span data-testid={`section-signal-${ev.prospect?.slug}-${ev.section}`}>
        <strong className="text-ink-900">{p.company_name}</strong> spent{" "}
        <span className={hot ? "text-red-500 font-bold" : "text-ink-900"}>{fmtSec(ev.seconds)}</span> on{" "}
        <em className="text-sun-500/90">{ev.section_label || ev.section}</em>
        {hot && <span className="ml-1.5 mono text-[9px] uppercase tracking-[0.14em] text-red-500 border border-red-500/40 rounded-full px-1.5 py-0.5">hot</span>}
      </span>
    );
  } else if (ev.kind === "note") {
    icon = <MessageSquare className="h-3.5 w-3.5 text-sun-500" />;
    line = <span><strong className="text-ink-900">{ev.name}</strong>{ev.company ? <span className="text-ink-900/65"> · {ev.company}</span> : null} left a note: <span className="text-ink-900/85 italic">"{(ev.message || '').slice(0, 90)}{(ev.message || '').length > 90 ? '…' : ''}"</span></span>;
  } else { return null; }
  return (
    <li className="flex items-start gap-3 py-3 border-b border-ink-900/8 last:border-0">
      <Avatar />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          {icon}
          <div className="text-sm text-ink-900/85 leading-snug flex-1 min-w-0">{line}</div>
        </div>
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/50 ml-5">{timeAgo(ev.at)}</div>
      </div>
    </li>
  );
}

export function ActivityFeed({ activity, busy, onRefresh }) {
  return (
    <section className="mt-14 border-t border-ink-900/10 pt-10" data-testid="activity-panel">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-sun-500 mb-2">
            <Activity className="h-3 w-3" /> live activity
            <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-sun-500 animate-pulse" />
          </div>
          <h2 className="serif font-light text-3xl md:text-4xl tracking-tight">Who's reading your deck.</h2>
          <p className="mt-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">Auto-refreshes every 20s · ping a prospect while you're top of mind</p>
        </div>
        <button onClick={onRefresh} data-testid="refresh-activity"
                className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors px-3 py-2 border border-ink-900/10 rounded">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {activity.length === 0 ? (
        <div className="border border-ink-900/10 rounded-md p-10 text-center bg-white/80/30 mono text-sm text-ink-900/55" data-testid="empty-activity">
          No activity yet · waiting for prospects to open the deck.
        </div>
      ) : (
        <ul className="border border-ink-900/10 rounded-md p-2 bg-white/55 backdrop-blur-sm divide-y divide-cream/8" data-testid="activity-list">
          {activity.map((ev, i) => <ActivityRow key={`${ev.kind}-${i}-${ev.at}`} ev={ev} />)}
        </ul>
      )}
    </section>
  );
}
